import {
  IErrorCrud,
  IErrorInfo,
} from 'legacy/lut/components/widgets/public/onboarding/requestsInfo/OnboardingUpload'
import { IFileSettings } from 'legacy/lut/library/dragNDrop'
import { appGetState } from 'legacy/lut/store'
import { useCallback, useState } from 'react'

import { Static } from 'core-components/types'
import { getTokens } from 'core/classes/helpers/StorageHelper'
import { Crud } from 'core/types/Crud'
import Logger from 'core/utils/Logger'
import { fail, success } from 'onboarding/classes/services/notification'
import settings from 'src/config/settings'

type UploadOptions = {
  filesInfo: IFileSettings[]
  endpoint: string
}

export async function uploadFiles(options: UploadOptions) {
  const url = `${settings.httpsServices.onboarding}${options.endpoint}`

  const [accessToken] = await getTokens(appGetState().app.ownerId)

  const formData = new FormData()

  const fileNames: IObjectAny = {}
  options.filesInfo.forEach((item, index) => {
    fileNames[`file${index}`] = item.file.name
    formData.append(`file${index}`, item.file, item.file.name)
  })

  const init: RequestInit = {
    headers: {
      Authorization: `${accessToken}`,
    },
    method: 'PUT',
    body: formData,
  }

  try {
    const response = await fetch(url, init)

    return {
      response,
      fileNames,
    }
  } catch (e) {
    Logger.error(e)
    return
  }
}

async function createErrorInfo(
  result: Record<string, any>,
  fileNames: IObjectAny
): Promise<IErrorInfo[]> {
  const errors = [] as IErrorInfo[]

  if (typeof result['errors'] !== 'object') {
    throw new Error('Unexpected response format')
  }

  Object.keys(result['errors']).forEach((file) => {
    const staticValue: IErrorCrud[] = []
    Object.keys(result['errors'][file]).forEach((item) => {
      const currentItem = result['errors'][file][item]
      if (typeof currentItem === 'object') {
        currentItem.forEach((value: any, index: number) => {
          staticValue.push({
            id: `${file}_${item}_${index}`,
            row: item,
            field: value.field,
            message: value.message,
          })
        })
      }
    })

    const newItem: IErrorInfo = {
      fileName: fileNames[file],
      template: result['errors'][file].template,
    }

    if (staticValue.length) {
      newItem.source = new Crud<IErrorCrud>({
        fetcher: new Static<IErrorCrud>(staticValue),
        keyField: 'id',
      })
    }

    errors.push(newItem)
  })

  return errors
}

async function parseResponseBody(response: Response): Promise<Record<string, any> | null> {
  // Some responses have an empty body (e.g. 401, or legacy handlers that `send()`
  // nothing) or a non-JSON body. Read defensively so a missing body is treated as
  // "no body" instead of throwing an unhandled SyntaxError on `.json()`.
  const text = await response.text()
  if (!text) {
    return null
  }

  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

const MSG_PARTIAL_FAILURE = 'Some files could not be processed'
const MSG_UPLOAD_ERROR = 'File upload error'

export function useFileUpload(endpoint: string) {
  const [errorInfo, setErrorInfo] = useState<IErrorInfo[] | null>(null)
  const [isLoading, setLoading] = useState(false)

  const upload = useCallback(
    async <T extends Record<string, any>>(filesInfo: IFileSettings[]): Promise<T | null> => {
      setLoading(true)
      const result = await uploadFiles({
        filesInfo,
        endpoint,
      })
      setLoading(false)

      if (!result) {
        // Network / fetch-level failure (uploadFiles already logged it).
        fail(MSG_UPLOAD_ERROR)
        return null
      }

      const { response, fileNames } = result
      const responseJson = await parseResponseBody(response)

      // Field-level validation errors (`{ status: 'error', errors }`) can arrive
      // with either a 2xx or a 4xx status — surface them richly first.
      if (responseJson?.status === 'error') {
        try {
          setErrorInfo(await createErrorInfo(responseJson, fileNames))
        } catch {
          fail(responseJson.message || MSG_UPLOAD_ERROR)
        }
        return responseJson as T
      }

      // An HTTP failure must never read as success, whatever the body says. This is
      // the hole being closed: a 4xx/5xx — or the unified endpoint's 422 partial
      // failure, whose body is `{ status: 'success' }` — previously fell through to
      // the "Requests Created" toast. 422 is a partial success (the persisted files
      // are in the body), so return it for tracking; any other failure returns null.
      if (!response.ok) {
        fail(
          response.status === 422 ? MSG_PARTIAL_FAILURE : responseJson?.message || MSG_UPLOAD_ERROR
        )
        return response.status === 422 ? (responseJson as T) : null
      }

      success('Requests Created')
      return (responseJson as T) ?? null
    },
    [endpoint]
  )

  return {
    isLoading,
    errorInfo,
    upload,
  }
}
